import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaBoxes, FaMoneyBillWave } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import SaveButton from '~/components/Buttons/Save';
import Toggle from '~/components/Toggle';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Wrapper, ToggleWrapper } from './styles';

interface IFormSponsorship {
  name: string;
  active: boolean;
  requires_req_files: boolean;
  requires_art_approval: boolean;
}

interface ILocation {
  id: string;
  name: string;
  active: boolean;
  requires_req_files: boolean;
  requires_art_approval: boolean;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
});

const New: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, control, errors } = useForm<IFormSponsorship>(
    {
      resolver: yupResolver(schema),
      defaultValues: {
        active: state?.active,
        requires_req_files: state?.requires_req_files || false,
        requires_art_approval: state?.requires_art_approval || false,
      },
    },
  );

  const submitForm = useCallback(
    async (data: IFormSponsorship) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/items/sponsorships', data);

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Patrocínio cadastrado com sucesso.',
          });
        } else {
          await api.put(`/items/sponsorships`, { id: state.id, ...data });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Patrocínio atualizado com sucesso.',
          });
        }

        history.push('/items/sponsorships');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  const onExit = () => {
    history.push('/items/sponsorships');
  };

  return (
    <Container>
      <BreadCrumb
        main="Itens"
        path="/items/main"
        firstChildPath="/items/sponsorships"
        firstChild="Tipos de Pagamentos"
        secondChild={
          state === undefined
            ? 'Novo Tipo de Pagamento'
            : 'Edição de Tipo de Pagamento'
        }
        mainIcon={FaBoxes}
        firstChildIcon={FaMoneyBillWave}
        secondChildIcon={FaMoneyBillWave}
        mainColored
        firstChildColored
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <div
          style={{
            maxWidth: 1000,
            display: 'flex',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <Wrapper>
            <DefaultInput
              name="name"
              labelFor="name"
              labelText="Nome"
              type="text"
              defaultValue={state?.name}
              error={errors?.name?.message}
              ref={register}
              maxLength={60}
            />
          </Wrapper>

          <ToggleWrapper>
            <Controller
              render={props => (
                <Toggle
                  label="Exige Arquivos de Requisição"
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="requires_req_files"
              defaultValue
              control={control}
            />
          </ToggleWrapper>

          <ToggleWrapper>
            <Controller
              render={props => (
                <Toggle
                  label="Exige Aprovação de Arte"
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="requires_art_approval"
              defaultValue
              control={control}
            />
          </ToggleWrapper>

          <ToggleWrapper>
            <Controller
              render={props => (
                <Toggle
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="active"
              defaultValue
              control={control}
            />
          </ToggleWrapper>
        </div>

        <div
          style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end' }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma gravação do patrocínio?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default New;
